import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import confetti from "canvas-confetti";
import api from "../lib/api";
import { SearchSuggest } from "../components/SearchSuggest";

const RES_STYLE = { HR: "bg-[#a855f7] text-black", HIT: "bg-[#22c55e]/25 text-[#22c55e]", BB: "bg-sky-500/20 text-sky-300", OUT: "bg-red-500/20 text-red-400" };

export default function HRSimulator() {
  const { data: playersData } = useQuery({ queryKey: ["players"], queryFn: () => api.get("/players").then((r) => r.data) });
  const players = playersData?.players || (Array.isArray(playersData) ? playersData : []);
  const [q, setQ] = useState("");
  const [pick, setPick] = useState(null);
  const [sim, setSim] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const opts = q.length >= 2 ? players.filter((p) => p.name.toLowerCase().includes(q.toLowerCase())).slice(0, 8).map((p) => p.name) : [];
  const run = async () => {
    if (!pick) return;
    setSpinning(true);
    setSim(null);
    try {
      const { data } = await api.get(`/sim/hr?mlb_id=${pick.mlb_id}`);
      setTimeout(() => {
        setSim(data);
        setSpinning(false);
        if (data.homered) confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 } });
      }, 900);
    } catch {
      setSpinning(false);
    }
  };
  return (
    <div className="space-y-5">
      <div className="fade-up">
        <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">HR <span className="gold-text">Simulator</span></h1>
        <p className="text-neutral-500 text-sm mt-1">Pick a batter and simulate his 4 at-bats tonight — powered by his live model HR probability. Will he go yard?</p>
      </div>
      <div className="card-surface rounded-xl p-4 max-w-xl fade-up-1">
        <SearchSuggest
          value={q}
          onChange={setQ}
          onPick={(n) => { const p = players.find((x) => x.name === n); setPick(p); setQ(n); setSim(null); }}
          options={opts}
          placeholder="Search a batter..."
          testid="sim-player-search"
        />
        {pick && (
          <div className="flex items-center gap-3 mt-3">
            <img src={pick.headshot} alt="" className="w-10 h-10 rounded-full object-cover bg-white/5" />
            <div className="flex-1">
              <div className="font-display font-bold uppercase">{pick.name} <span className="text-neutral-500 text-xs">{pick.team}</span></div>
              <div className="text-[11px] text-neutral-500">vs {pick.matchup?.pitcher || "TBD"}{pick.matchup?.hand ? ` (${pick.matchup.hand}HP)` : ""} · HR prob tonight: <b className="text-[#a855f7]">{pick.stats?.hr_probability}%</b></div>
            </div>
            <button onClick={run} disabled={spinning} data-testid="sim-run-btn"
              className="bg-[#a855f7] hover:bg-[#9333ea] disabled:opacity-50 text-black font-black uppercase tracking-wider text-xs px-5 py-2.5 rounded transition-colors duration-200">
              {spinning ? "Batting..." : "Play 4 ABs"}
            </button>
          </div>
        )}
      </div>
      {spinning && <div className="font-chalk text-2xl text-neutral-400 animate-pulse" data-testid="sim-spinning">Stepping up to the plate...</div>}
      {sim && (
        <div className="space-y-3 fade-up-1" data-testid="sim-result">
          <div className="grid sm:grid-cols-4 gap-3">
            {sim.abs.map((ab) => (
              <div key={ab.ab} className="card-surface rounded-xl p-4 text-center" style={{ animationDelay: `${ab.ab * 0.12}s` }} data-testid={`sim-ab-${ab.ab}`}>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">AB {ab.ab}</div>
                <div className={`inline-block mt-2 text-sm font-black uppercase px-3 py-1 rounded-full ${RES_STYLE[ab.result]}`}>{ab.result}</div>
                <div className="text-xs text-neutral-400 mt-2">{ab.text}</div>
              </div>
            ))}
          </div>
          <div className={`card-surface rounded-xl p-4 text-center ${sim.homered ? "border-[#a855f7] gold-glow" : ""}`}>
            <div className="font-chalk text-3xl">
              {sim.homered ? <span className="gold-text">💣 {sim.player} GOES YARD in the sim!</span> : <span className="text-neutral-400">No homer this sim — run it back?</span>}
            </div>
          </div>
          <p className="text-[10px] text-neutral-600">{sim.note}</p>
        </div>
      )}
    </div>
  );
}
